import moment, { Moment } from "moment";
import { AssetGroupAvailability, AvailabilitySlot, SUPAssetGroupAvailability, SUPAvailabilityTimeSlot } from "../../../types/assetGroup";
import { i18n } from "../../../i18n/i18n";
import { STATION_TIME_ZONE, atStation, stationSlotStart } from "../../../util/stationTime";

/**
 * A slot as shown, selected and looked up: the station wall-clock time,
 * "HH:mm". The availability is keyed by station day ("DD.MM.YYYY") and
 * station time — so a slot is never converted to the phone's clock. These
 * helpers used to show every slot in `moment.tz.guess()` (a London phone saw
 * 09:00 as 08:00; a Tokyo phone saw the 18:00 slot as 01:00) and convert the
 * pick back, while the request was composed from that phone-local time on the
 * picked date — a different instant. Normalising through the parse keeps the
 * one non-"HH:mm" key the backend emits (a full-day group's "0") reading as
 * "00:00", exactly as before.
 *
 * Every helper here reads the station's clock in `timeZone`, the zone of the
 * location being booked (#3219; `useSelectedLocationTimeZone`), and defaults to
 * `STATION_TIME_ZONE`.
 */
const stationSlotKey = (
    selectedDate: moment.Moment,
    time: string,
    timeZone: string = STATION_TIME_ZONE
): string => stationSlotStart(selectedDate, time, timeZone).format('HH:mm');

/**
 * Returns all time slots on the given date with at least one duration that has at least one available board
 *
 * @param availability
 * @param selectedDate
 */
export const getAvailableTimeSlots = (
    availability: AssetGroupAvailability,
    selectedDate: moment.Moment,
    timeZone: string = STATION_TIME_ZONE
): string[] => {
    const dateKey = selectedDate.format('DD.MM.YYYY');
    if (!availability.hasOwnProperty(dateKey)) {
        return [];
    }
    const filteredTimeSlots = Object.keys(availability[dateKey])
        .map(timeKey => stationSlotKey(selectedDate, timeKey, timeZone))
        .sort();
    return filteredTimeSlots;
};

/**
 * Returns all time slots on the given date with at least one duration that has at least one available board
 *
 * @param availability
 * @param selectedDate
 */
export const getSUPAvailableTimeSlots = (
    availability: SUPAssetGroupAvailability,
    selectedDate: moment.Moment,
    timeZone: string = STATION_TIME_ZONE
  ): string[] => {


    const dateKey = selectedDate.format('DD.MM.YYYY');
    if (!availability.hasOwnProperty(dateKey)) {
      return [];
    }

    const filteredTimeSlots = Object.keys(availability[dateKey])
      .filter(timeKey => {
        const timeSlot = availability[dateKey][timeKey];
        // Check if at least one duration has count > 0
        return Object.values(timeSlot).some(slot => slot.count > 0);
      })
      .map(timeKey => stationSlotKey(selectedDate, timeKey, timeZone))
      .sort();

    return filteredTimeSlots;
  };

export const getSUPAvailableData = (
    availability: SUPAssetGroupAvailability,
    selectedDate: moment.Moment,
    selectedTime: string,
    timeZone: string = STATION_TIME_ZONE
) => {
    const dateKey = selectedDate.format('DD.MM.YYYY');
    if (!availability.hasOwnProperty(dateKey)) {
        return {};
    }

    const timeKey = stationSlotKey(selectedDate, selectedTime, timeZone);
    const availableTimeSlots = availability[dateKey];

    if (!availableTimeSlots || !availableTimeSlots[timeKey]) {
        return {};
    }

    const durationTimeSlot: SUPAvailabilityTimeSlot = availability[dateKey][timeKey];

    const durations: number[] = [];
    const priceMap: Record<number, number> = {};
    const limitsMap: Record<number, number> = {};

    for (const [durationStr, slot] of Object.entries(durationTimeSlot)) {
        const duration = parseInt(durationStr, 10);
        if (slot?.count && slot.count > 0) {
            priceMap[duration] = slot.price;
            limitsMap[duration] = slot.count;
            durations.push(duration);
        }
    }

    return {
        durations,
        priceMap,
        limitsMap
    }

}

/**
 * Returns all durations on the given date and time-slot with at least on available board
 *
 * @param availability
 * @param selectedDate
 * @param selectedTime
 */
export const getAvailableDurations = (
    availability: AssetGroupAvailability,
    selectedDate: moment.Moment,
    selectedTime: string,
    timeZone: string = STATION_TIME_ZONE
): number[] => {
    const dateKey = selectedDate!.format('DD.MM.YYYY');
    if (!availability.hasOwnProperty(dateKey)) {
        return [];
    }
    const timeSlot: AvailabilitySlot =
        availability[dateKey][stationSlotKey(selectedDate, selectedTime, timeZone)];

    const durations: number[] = [];

    for (const duration in timeSlot) {
        if (timeSlot[duration] < 1) {
            // either the duration does not exist or there are no assets available for it
            continue;
        }
        durations.push(Number(duration));

        // const durationValue = availability[dateKey][selectedTime].duration;

        // // Note: For partner USC only a duration of 1 hour is available
        // if (isPartnerUsc && durationValue !== 1) {
        //     continue;
        // }

        // if (!durations.includes(durationValue)) {
        //     durations.push(durationValue);
        // }
    }

    // TODO check why a custom sort is needed
    return durations.sort((a, b) => a - b);
};



export const getTimeFormat = (date: Moment) => {
    const currentLanguage = i18n.language;
    if (currentLanguage === 'en') {
        return date.format('hh:mm A');
    } else {
        return date.format('HH:mm');
    }
};

/**
 * Whether a rental starting at the station slot `time` on the picked day ends,
 * `duration` minutes later, on one of `skipDays` — the weekday read on the
 * STATION's calendar. It was read in the phone's zone from the phone-local
 * time, which differs from the station's across a DST change the two zones do
 * not share (a New York phone's Sunday 01:00 + 23 h ends on Monday; at the
 * station it is still Sunday).
 */
export const endsOnSkippedDay = (
  date: moment.Moment,
  time: string,
  duration: number,
  skipDays: readonly string[],
  timeZone: string = STATION_TIME_ZONE
): boolean => {
  const end = stationSlotStart(date, time, timeZone).add(duration, "minutes");
  return skipDays.includes(end.locale("en").format("ddd").toLowerCase());
};

/**
 * The next 60 days a parcel can be dropped off, as "DD.MM.YYYY" labels,
 * starting with the STATION's today. It started with the phone's today: a
 * Tokyo phone after midnight there dropped the station's still-current day, a
 * New York phone in the evening offered one the station had already left.
 * (Generate dates for the next 60 days — adjust this range as needed.)
 */
export const parcelAvailableDates = (
  now: moment.MomentInput = Date.now(),
  timeZone: string = STATION_TIME_ZONE
): string[] => {
  const today = atStation(now, timeZone).startOf("day");
  const dates: string[] = [];
  for (let i = 0; i < 60; i++) {
    dates.push(today.clone().add(i, "days").format("DD.MM.YYYY"));
  }
  return dates;
};
